import { IndeterminateCheckBox } from '@mui/icons-material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import Checkbox from '@mui/material/Checkbox';
import TablePagination from '@mui/material/TablePagination';
import CopyToClipboardWrapper from 'components/CopyToClipboardWrapper';
import { ErrorBoundary } from 'components/Errors/ErrorBoundary';
import IconButton from 'components/IconButton';
import LoadingScreen from 'components/LoadingScreen';
import ModalDialog from 'components/ModalDialog';
import NoRecordFound from 'components/NoRecordFound/NoRecordFound';
import Tooltip from 'components/Tooltip';
import moment from 'moment';
import React, { Suspense, useEffect, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import './styles.css'; // Import your CSS file for styling
import { Row, Col } from 'reactstrap';

import ColumnSelector from './ColumnSelector';

export const DATA_GRID_PLACEHOLDER = '';

/**
 * Configuration interface for pagination settings in the DataGrid.
 */
export interface PaginationConfig {
  /**
   * Total number of items in the dataset.
   */
  total_count: number;

  /**
   * Current page number.
   */
  pageNumber: number;

  /**
   * Number of items per page.
   */
  pageSize: number;

  /**
   * Callback function invoked when the page changes.
   * @param newPage The new page number.
   */
  onPageChange: (newPage: number) => void;

  /**
   * Callback function invoked when the page size changes.
   * @param newPageSize The new page size.
   */
  onPageSizeChange?: (newPageSize: number) => void;

  /**
   * Options for rows per page. If set to an empty array, the dropdown will be disabled.
   */
  rowsPerPageOptions?: number[];
}

/**
 * Configuration interface for sorting settings in the DataGrid.
 */
export interface SortingConfig {
  /**
   * The key by which the data is sorted.
   */
  orderBy: string;

  /**
   * Sorting order, either 'asc' (ascending), 'desc' (descending), or undefined (initial state).
   */
  order: 'asc' | 'desc' | undefined;

  /**
   * Callback function invoked when the sorting key changes.
   * @param value The new sorting key.
   */
  onOrderByChange: (value: string) => void;

  /**
   * Callback function invoked when the sorting order changes.
   * @param value The new sorting order.
   */
  onOrderChange: (value: 'asc' | 'desc') => void;
}

/**
 * Configuration interface for column display settings in the DataGrid.
 */
export interface ColumnDisplayConfig {
  /**
   * Array of keys for the columns that are currently enabled/displayed.
   */
  enabledColumnKeys: string[];

  /**
   * Default columns to display, allow reset back to this configuration
   */
  defaultColumnKeys?: string[];

  /**
   * The keys to be excluded from the column selector display
   */
  keysToBeExcluded?: string[];

  /**
   * Callback function invoked when the column display settings change.
   * if specified, then a customization option will be rendered under the grid
   * @param updatedColumnKeys The updated array of column keys.
   */
  onColumnsDisplayChange?: (updatedColumnKeys: string[]) => void;
}

/**
 * Configuration interface for row selection in the DataGrid.
 */
export interface RowSelectionConfig {
  /**
   * Enable cross-page selection.
   */
  enableSelectionAcrossPages?: boolean;
  /**
   * list of identifier keys for pre selected rows.
   */
  preSelectedRows: number[];

  /**
   * A callback function invoked when the selection of rows changes.
   * @param selectedRowIds An array of strings representing the unique identifiers of the selected rows.
   */
  onSelectionChange: (selectedRowIds: number[]) => void;
}

export interface ClassRule {
  /**
   * The rule function, if evaluates to true, the respective css class will get applied to the that row
   */
  ruleFunction: (rowData) => boolean;

  /**
   * The class name to be applied if the rule function evaluates to true
   */
  className: string;
}

export interface RowConfig {
  /**
   * CSS properties for styling the row cell (optional).
   */
  cssStyle?: React.CSSProperties;

  /**
   * CSS class name for styling the row cell (optional).
   */
  className?: string;

  /**
   * configure rules to apply dynamic CSS classes for styling the row (optional).
   */
  classRules?: ClassRule[];
}

/**
 * Interface for defining column configuration in the DataGrid.
 */
export interface ColumnConfig {
  /**
   * Unique key for the column.
   */
  key: string;

  /**
   * Label or title for the column.
   */
  label: string;

  /**
   * Indicates whether the column is sortable (optional).
   */
  sortable?: boolean;

  /**
   * order by key (optional).
   */
  orderBy?: string;

  /**
   * CSS properties for styling the header cell (optional).
   */
  headerCellStyle?: React.CSSProperties;

  /**
   * CSS class name for styling the header cell (optional).
   */
  headerCellClassName?: string;

  /**
   * CSS properties for styling the row cell (optional).
   */
  rowCellStyle?: React.CSSProperties;

  /**
   * CSS class name for styling the row cell (optional).
   */
  rowCellClassName?: string;

  /**
   * CSS properties for styling the content (optional).
   */
  contentStyle?: React.CSSProperties;

  /**
   * CSS class name for styling the content (optional).
   */
  contentClassName?: string;

  /**
   * column cell tooltip (optional).
   */
  tooltip?: (rowData: any) => string | React.ReactNode | undefined;

  /**
   * column header tooltip (optional).
   */
  headerTooltip?: string | React.ReactNode | undefined;

  /**
   * Function to render custom content for the cell (optional).
   */
  render?(rowData: any): React.ReactNode;

  /**
   * Function to get custom content for the cell (optional).
   * when this is defined, then this will be rendered and the original content will be ignored
   */
  overrideContents?(rowData: any): React.ReactNode;

  /**
   * Callback function invoked when the cell is clicked (optional).
   */
  onClick?: (rowData: any) => void;

  /**
   * configure rules to apply dynamic CSS classes for styling the cell (optional).
   */
  classRules?: ClassRule[];

  /**
   * Enable copy to clipboard feature for the cell (optional).
   * The valueToCopy will be deduced by column.key if not provided specifically
   * NOTE: you can either provide enableCopyToClipboard or valueToCopy, do not need to provide both
   */
  enableCopyToClipboard?: boolean;

  /**
   * Override the value to be copied to the clipboard for this cell. (optional).
   * NOTE: you can either provide enableCopyToClipboard or valueToCopy, do not need to provide both
   */
  valueToCopy?: ((rowData) => string) | string;

  /**
   * Maximum number of characters for string values in the cell (optional).
   * If the value length exceeds this number, it will be auto-trimmed and a tooltip will show the full text.
   * Default value is 30 characters.
   * NOTE: if we use overrideContents, or render custom contents, then this will not be applied
   * NOTE: related fixed width value approximate 6.50 pixels per character for the default font size
   */
  maxCharLength?: number;

  /**
   * Width of the column. Can be a fixed value (e.g., '100px') or 'auto' for auto-expansion.
   * NOTE: for fixed value approximate 6.50 pixels per character for the default font size
   */
  width?: string;
}

/**
 * Configuration interface for the DataGrid component.
 */
export interface DataGridConfig {
  /**
   * Key field that uniquely identifies the each row in the data
   */
  rowIdKey: string;
  /**
   * Array of column configurations.
   */
  columns: ColumnConfig[];

  /**
   * Row styles configuration
   */
  rowConfig?: RowConfig;

  /**
   * Configuration for pagination settings (optional).
   */
  paginationConfig?: PaginationConfig;

  /**
   * Configuration for sorting settings (optional).
   */
  sortingConfig?: SortingConfig;

  /**
   * Configuration for column display settings (optional).
   */
  columnsDisplayConfig?: ColumnDisplayConfig;

  /**
   * Configuration for row selection. (optional).
   */
  rowSelectionConfig?: RowSelectionConfig;

  /**
   * DataGrid container CSS styles
   */
  cssStyles?: React.CSSProperties;

  /**
   * DataGrid container CSS classes
   */
  cssClassName?: string;
}

/**
 * Props interface for the DataGrid component.
 */
export interface DataGridProps {
  /**
   * Configuration for the DataGrid.
   */
  config: DataGridConfig;

  /**
   * Data to be displayed in the DataGrid.
   */
  data?: any[];

  disabled?: boolean;
  loading?: boolean;
}

interface SelectAllCheckboxProps {
  listingData: any[] | undefined;
  rowIdKey: string;
  preSelectedRows: number[];
  onSelectionChange: (selectedRowIds: number[]) => void;
  enableSelectionAcrossPages?: boolean;
}

const SelectAllCheckbox: React.FC<SelectAllCheckboxProps> = ({
  listingData,
  rowIdKey,
  preSelectedRows,
  onSelectionChange,
  enableSelectionAcrossPages = false,
}) => {
  const [indeterminate, setIndeterminate] = useState<boolean>(false);
  const [checked, setChecked] = useState<boolean>(false);

  useEffect(() => {
    if (listingData) {
      const currentPageIds = listingData.map((row) => row[rowIdKey]);
      const currentPageSet = new Set(currentPageIds);

      // Check if all current page items are selected
      const currentPageSelected = currentPageIds.every((id) =>
        preSelectedRows.includes(id),
      );

      // For cross-page selection, we need to check if there are any selections outside current page
      const hasOtherPageSelections =
        enableSelectionAcrossPages &&
        preSelectedRows.some((id) => !currentPageSet.has(id));

      // For the indeterminate state, we need:
      // 1. Some but not all current page items selected, OR
      // 2. No current page items selected but has other page selections
      const someCurrentPageSelected = currentPageIds.some((id) =>
        preSelectedRows.includes(id),
      );

      setChecked(currentPageSelected);
      setIndeterminate(
        (!currentPageSelected && someCurrentPageSelected) || // Some current page items selected
          (!currentPageSelected && hasOtherPageSelections), // No current page items but has other page selections
      );
    }
  }, [listingData, preSelectedRows, rowIdKey, enableSelectionAcrossPages]);

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    if (!listingData) return;

    const currentPageIds = listingData.map((row) => row[rowIdKey]);

    if (enableSelectionAcrossPages) {
      if (isChecked) {
        // Add current page IDs to existing selection
        const newSelection = new Set([...preSelectedRows, ...currentPageIds]);
        onSelectionChange(Array.from(newSelection));
      } else {
        // Remove current page IDs from selection
        const currentPageIdSet = new Set(currentPageIds);
        onSelectionChange(
          preSelectedRows.filter((id) => !currentPageIdSet.has(id)),
        );
      }
    } else {
      // Without cross-page selection, just select/deselect current page
      onSelectionChange(isChecked ? currentPageIds : []);
    }
  };

  return (
    <Tooltip
      id="select-all-tooltip"
      tooltip="Select/Deselect all rows on current page"
    >
      <Checkbox
        id="select-all"
        checked={checked}
        indeterminate={indeterminate}
        onChange={handleSelectAll}
        icon={<CheckBoxOutlineBlankIcon />}
        checkedIcon={<CheckBoxIcon />}
        indeterminateIcon={<IndeterminateCheckBox />}
        color="primary"
        style={{ padding: 5 }}
      />
    </Tooltip>
  );
};

/**
 * Functional component representing a DataGrid.
 * The DataGrid component is used to render tabular data with features such as pagination, sorting, column display customization,
 * and row selection. It provides a flexible interface for configuring column settings, handling user interactions, and rendering
 * dynamic content within each cell of the table.
 *
 * @param config Configuration object for the DataGrid, including column settings, pagination, sorting, column display, and row selection.
 * @param data Data to be displayed in the DataGrid.
 */
const DataGrid: React.FC<DataGridProps> = ({
  config,
  data,
  disabled,
  loading,
}) => {
  // const uniqueId = useMemo(() => {
  //   return Math.random().toString(36).substr(2, 9); // Example of generating a unique ID
  // }, []);
  const uniqueId = Math.random().toString(36).substr(2, 9); // Example of generating a unique ID

  const {
    columns,
    rowConfig,
    paginationConfig,
    sortingConfig,
    columnsDisplayConfig,
    rowSelectionConfig,
  } = config;
  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  // console.log('paginationConfig: ', paginationConfig)

  /**
   * @returns returns true effective column keys
   */
  const getColumnKeys = (): string[] => {
    return columnsDisplayConfig
      ? columnsDisplayConfig.enabledColumnKeys
      : columns.map((column) => column.key);
  };

  const handleColumnsUpdate = (updatedColumnKeys) => {
    if (columnsDisplayConfig?.onColumnsDisplayChange) {
      columnsDisplayConfig.onColumnsDisplayChange(updatedColumnKeys);
    }
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    // its zero based page index in TablePagination, while django uses 1 based index
    // so increase number here to compensate that
    if (paginationConfig) paginationConfig.onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newValue = parseInt(event.target.value) || 25;
    if (paginationConfig?.onPageSizeChange)
      paginationConfig.onPageSizeChange(newValue);
  };

  const getSortOrderKey = (column: ColumnConfig) =>
    column.orderBy ? column.orderBy : column.key;

  const handleSort = (column: ColumnConfig) => {
    if (sortingConfig) {
      const sortOrderKey = getSortOrderKey(column);

      sortingConfig.onOrderByChange(sortOrderKey);

      const isAsc =
        sortingConfig.orderBy === sortOrderKey && sortingConfig.order === 'asc';
      sortingConfig.onOrderChange(isAsc ? 'desc' : 'asc');

      // console.log({
      //   msg: 'sort order updated.',
      //   sortOrderKey,
      //   asc: isAsc,
      // });
    }
  };

  const getCellContents = (rowData, column: ColumnConfig) => {
    // console.log('overrideContents', column.overrideContents)
    return column.overrideContents
      ? column.overrideContents(rowData)
      : rowData[column.key];
  };

  const renderCell = (rowData: any, column: ColumnConfig) => {
    // Extract the content for the current cell based on the column key
    const cellContent = getCellContents(rowData, column);

    // if(column.onClick){
    //   console.log({
    //     msg: 'in the render cell',
    //     column: column,
    //     rowData,
    //     onClick: column.onClick
    //   });
    // }

    // If a custom render function is provided for the column, use it to render the cell
    if (column.render) {
      return (
        <td
          key={column.key}
          style={{ ...column.rowCellStyle }} // Apply custom row style
          className={column.rowCellClassName} // Apply custom row className
          onClick={() => column.onClick && column.onClick(rowData)}
        >
          {column.contentClassName ? (
            <span className={column.contentClassName}>
              {column.render(rowData)}
            </span>
          ) : (
            column.render(rowData)
          )}
        </td>
      );
    }

    // Initialize variables for updated content, CSS classes, and tooltip content
    let updatedContents = cellContent;
    let cssClasses: string[] = [];
    let calculatedTooltipContent: string | undefined;

    // Calculate maxCharLength dynamically]
    const defaultMaxCharLength = 30; // Default value
    const maxCharLength = column.maxCharLength ?? defaultMaxCharLength;

    // Check if a CSS class is specified in the column configuration
    if (column.rowCellClassName) {
      cssClasses.push(column.rowCellClassName);
    }

    const cellContentType = typeof cellContent;
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    const isUrl = urlRegex.test(cellContent);

    // Handle Number Values: Add text alignment class for numeric values
    if (typeof cellContent === 'number') {
      cssClasses.push('number-column');
    }

    // Handle Boolean Values: Render checkbox icons for boolean values
    else if (cellContentType === 'boolean') {
      updatedContents = cellContent ? (
        <CheckBoxIcon />
      ) : (
        <CheckBoxOutlineBlankIcon />
      );
      cssClasses.push('bool-column');
    }
    // Handle Null or Empty Values: Render placeholder for null or empty values
    else if (cellContent === null || cellContent === '') {
      updatedContents = DATA_GRID_PLACEHOLDER;
      cssClasses.push('empty-column');
    }

    // Handle Date Values: Check if the string is a valid ISO 8601 date and format it
    else if (
      cellContentType === 'string' &&
      cellContent.length > 8 && // Ensure the string is long enough to be a date
      moment(cellContent, moment.ISO_8601, true).isValid() // Validate the date format
    ) {
      updatedContents = moment(cellContent).format('lll'); // Format the date to a readable format
      cssClasses.push('date-column'); // Add a CSS class for date columns
    }

    // Handle Long Text or Overflow: Truncate long text and add tooltip if necessary
    else if (cellContentType === 'string') {
      if (cellContent.length > maxCharLength) {
        updatedContents = `${cellContent.substring(0, maxCharLength)}...`;
        calculatedTooltipContent = cellContent;
        cssClasses.push('trimmed-text-column');
      }
    }

    if (cellContentType === 'string' && isUrl) {
      updatedContents = (
        <a href={cellContent} target="_blank" rel="noopener noreferrer">
          {updatedContents}
        </a>
      ); // Open link in new tab
    }

    // Check if onClick handler is defined, then add CSS classes for cursor and indication
    if (column.onClick) {
      cssClasses.push('hand-cursor');
      cssClasses.push('click-indicator');
    }

    // calculate the final tooltip contents to be displayed
    let tooltipContents: React.ReactNode = calculatedTooltipContent;

    if (column.tooltip || calculatedTooltipContent) {
      if (column.tooltip) {
        // Get tooltip content using provided function or undefined
        const columnTooltip: React.ReactNode | undefined = column.tooltip
          ? column.tooltip(rowData)
          : undefined;

        if (columnTooltip) {
          // Render the tooltip content to a string
          const columnTooltipStr: string = React.isValidElement(columnTooltip)
            ? ReactDOMServer.renderToStaticMarkup(columnTooltip)
            : String(columnTooltip);

          // Check if columnTooltip already contains calculatedTooltipContent
          if (typeof columnTooltipStr === 'string') {
            tooltipContents =
              calculatedTooltipContent &&
              !columnTooltipStr.includes(String(calculatedTooltipContent)) ? (
                <>
                  {calculatedTooltipContent}
                  <br />
                  {columnTooltip}
                </>
              ) : (
                columnTooltip
              );
          } else {
            // eslint-disable-next-line no-console
            console.error(
              'Should not be here: columnTooltipStr is not a valid string, ignoring the calculatedTooltipContent...',
            );
            tooltipContents = columnTooltip;
          }

          cssClasses.push('tooltip-indicator');
        }
        // TODO: This will cause flood logging on console in case when tooltip function is defined on the config but the function itself return undefined conditionally
        // else {
        //   console.error('Tooltip is expected for column ' + column.key);
        // }
      }
    }

    // column rule css classes,  apply class rules
    if (column.classRules) {
      for (let rule of column.classRules) {
        if (rule.ruleFunction(rowData)) {
          cssClasses.push(rule.className);
        }
      }
    }

    // disable log
    // console.log("Render Column data: ", {
    //   key: column.key,
    //   headerCellStyle: column.rowCellStyle,
    //   rowCellClassName: column.rowCellClassName,
    //   tooltipContents,
    //   updatedContents,
    //   cellContent,
    //   cssClasses,
    //   onclick: column.onClick,
    // });

    // Handle Value to Copy: Add copy to clipboard icon for specified cell values
    const copyAbleContents = (column: ColumnConfig, rowData, contents) => {
      let textToCopy = column.enableCopyToClipboard
        ? rowData[column.key]
        : undefined;
      if (column.valueToCopy) {
        if (typeof column.valueToCopy === 'string') {
          textToCopy = column.valueToCopy;
        }
        if (typeof column.valueToCopy === 'function') {
          textToCopy = column.valueToCopy(rowData);
        }
      }
      return textToCopy ? (
        <CopyToClipboardWrapper valueToCopy={textToCopy}>
          {contents}
        </CopyToClipboardWrapper>
      ) : (
        contents
      );
    };

    return (
      <td
        key={column.key}
        style={{ ...column.rowCellStyle }} // Apply custom row style
        className={cssClasses.join(' ')} // Apply CSS classes
        onClick={() => column.onClick && column.onClick(rowData)}
      >
        {tooltipContents ? (
          <>
            {/* NOTE: A single ReactTooltip control is rendered inside the body to  handle all tooltips */}
            <Tooltip
              id={uniqueId}
              tooltip={tooltipContents}
              title={column.label}
            >
              {copyAbleContents(column, rowData, updatedContents)}
            </Tooltip>
          </>
        ) : (
          <>{copyAbleContents(column, rowData, updatedContents)}</>
        )}
      </td>
    );
  };

  const handleRowSelectionChange = (checked: boolean, rowId: number) => {
    if (!rowSelectionConfig) return;

    const { preSelectedRows, onSelectionChange, enableSelectionAcrossPages } =
      rowSelectionConfig;

    if (enableSelectionAcrossPages) {
      // With cross-page selection, add/remove individual row while keeping other selections
      const newSelection = new Set(preSelectedRows);
      if (checked) {
        newSelection.add(rowId);
      } else {
        newSelection.delete(rowId);
      }
      onSelectionChange(Array.from(newSelection));
    } else {
      // Without cross-page selection, just toggle the individual row
      const newSelection = checked
        ? [...preSelectedRows, rowId]
        : preSelectedRows.filter((id) => id !== rowId);
      onSelectionChange(newSelection);
    }
  };

  const getRowClasses = (rowData) => {
    const classes: string[] = [];

    // add selected rows styles
    if (rowSelectionConfig?.preSelectedRows) {
      const rowId = rowData[config.rowIdKey];
      if (rowSelectionConfig.preSelectedRows.includes(rowId)) {
        // check if not already applied
        if (!classes.includes('selected-row')) {
          classes.push('selected-row');
        }
      }
    }

    //  apply class rules
    if (rowConfig) {
      if (rowConfig.className) classes.push(rowConfig.className);

      if (rowConfig.classRules) {
        for (let rule of rowConfig.classRules) {
          if (rule.ruleFunction(rowData)) {
            classes.push(rule.className);
          }
        }
      }
    }

    return classes.join(' ');
  };

  const renderHeaderCell = (column: ColumnConfig) => {
    const headerStyle: React.CSSProperties = {
      ...column.headerCellStyle,
      ...(column.width && {
        width: column.width,
      }), // Apply width only if specified
      ...(column.sortable
        ? {
            cursor: 'pointer',
          }
        : { cursor: 'default' }),
    };

    return (
      <th
        key={column.key}
        onClick={() => column.sortable && handleSort(column)}
        style={headerStyle} // Apply custom header style
        className={column.headerCellClassName} // Apply custom header className
        scope="col"
      >
        {sortingConfig && column.sortable ? (
          <>
            {column.headerTooltip ? (
              <Tooltip id={uniqueId} tooltip={column.headerTooltip}>
                <div dangerouslySetInnerHTML={{ __html: column.label }} />
              </Tooltip>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: column.label }} />
            )}
            <i
              className={
                sortingConfig.orderBy === getSortOrderKey(column)
                  ? sortingConfig.orderBy === getSortOrderKey(column) &&
                    sortingConfig.order === 'desc'
                    ? 'fa fa-sort-amount-down'
                    : 'fa fa-sort-amount-up'
                  : 'fa fa-sort'
              }
            ></i>
          </>
        ) : column.headerTooltip ? (
          <Tooltip id={uniqueId} tooltip={column.headerTooltip}>
            {column.label}
          </Tooltip>
        ) : (
          column.label
        )}
      </th>
    );
  };

  return (
    <>
      <div
        className={`container-fluid p-0 ${config.cssClassName || ''}`}
        style={{ ...config.cssStyles }}
      >
        <div className="waitable-component">
          {/* Conditional rendering based on the disabled state */}
          {disabled && <div className="masking-layer"></div>}
          {loading && (
            <div className="masking-layer">
              <LoadingScreen mask={true} />
            </div>
          )}
          <div className="datagrid-container table-responsive">
            <table className="table tablesorter table-striped table-hover datagrid-table">
              <thead>
                <tr>
                  {rowSelectionConfig && (
                    <th className="selection_column" scope="col">
                      <SelectAllCheckbox
                        key={`select-all-key-${config.rowIdKey}`}
                        listingData={data}
                        rowIdKey={config.rowIdKey}
                        preSelectedRows={rowSelectionConfig.preSelectedRows}
                        onSelectionChange={rowSelectionConfig.onSelectionChange}
                        enableSelectionAcrossPages={
                          rowSelectionConfig.enableSelectionAcrossPages
                        }
                      />
                    </th>
                  )}
                  {getColumnKeys()?.map((key) => {
                    const column = columns.find((column) => column.key === key);
                    if (column) {
                      return renderHeaderCell(column); // Use the new renderHeaderCell function
                    }
                  })}
                </tr>
              </thead>
              <tbody style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {data?.map((rowData, index) => (
                  <tr key={index} className={getRowClasses(rowData)}>
                    {rowSelectionConfig && (
                      <th className="selection_column" scope="row">
                        <Checkbox
                          key={index}
                          name="selectAll"
                          id={`${config.rowIdKey}-${index}`}
                          color="primary"
                          checked={rowSelectionConfig.preSelectedRows?.includes(
                            rowData[config.rowIdKey],
                          )}
                          onChange={(event) => {
                            handleRowSelectionChange(
                              event.target.checked,
                              rowData[config.rowIdKey],
                            );
                          }}
                          style={{ padding: 5 }}
                        />
                      </th>
                    )}
                    {getColumnKeys()?.map((key) => {
                      const column = columns.find(
                        (column) => column.key === key,
                      );
                      if (column) {
                        return renderCell(rowData, column);
                      }
                    })}
                  </tr>
                ))}
                {!data && (
                  <tr key="data-grid-loading-tr">
                    <td
                      colSpan={
                        config.columns.length +
                        (config.rowSelectionConfig ? 1 : 0)
                      }
                    >
                      <LoadingScreen />
                    </td>
                  </tr>
                )}
                {data?.length === 0 && (
                  <tr key="data-grid-no record-tr">
                    <td
                      colSpan={
                        config.columns.length +
                        (config.rowSelectionConfig ? 1 : 0)
                      }
                    >
                      <NoRecordFound />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Row>
            <Col>
              {paginationConfig && (
                <ErrorBoundary suppressError="Invariant Violation">
                  <Suspense fallback={<></>}>
                    <TablePagination
                      rowsPerPageOptions={
                        paginationConfig.rowsPerPageOptions || [10, 25, 50, 100]
                      }
                      component="div"
                      count={paginationConfig.total_count}
                      rowsPerPage={paginationConfig.pageSize}
                      page={
                        // its zero based page index in TablePagination, while django uses 1 based index
                        // so decrease number here and we will compensate handleChangePage
                        paginationConfig.pageNumber
                      }
                      onPageChange={handleChangePage} // Use prop function for page change
                      onRowsPerPageChange={
                        paginationConfig.onPageSizeChange
                          ? handleChangeRowsPerPage
                          : undefined
                      }
                      style={{ width: '100%' }}
                    />
                  </Suspense>
                </ErrorBoundary>
              )}
            </Col>
            {columnsDisplayConfig?.onColumnsDisplayChange && (
              <Col
                className="col-auto"
                style={{
                  paddingTop: 10,
                }}
              >
                <IconButton
                  id="edit-column-display"
                  iconClass="fa fa-gear"
                  size="sm"
                  tooltip="Display options"
                  onClick={() => {
                    setDialogIsOpen(true);
                  }}
                />
              </Col>
            )}
          </Row>
        </div>
      </div>
      {columnsDisplayConfig?.onColumnsDisplayChange && dialogIsOpen && (
        <ModalDialog
          title={'Display Options'}
          subTitle={<p>Customize column display and order</p>}
          childComponent={
            <ColumnSelector
              columns={config.columns}
              enabledColumnKeys={columnsDisplayConfig.enabledColumnKeys}
              defaultColumnKeys={columnsDisplayConfig.defaultColumnKeys}
              keysToBeExcluded={columnsDisplayConfig.keysToBeExcluded}
              onColumnToggle={handleColumnsUpdate}
            />
          }
          onClose={() => {
            setDialogIsOpen(false);
          }}
          customFooter={
            columnsDisplayConfig.defaultColumnKeys ? (
              <>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    if (columnsDisplayConfig.onColumnsDisplayChange) {
                      columnsDisplayConfig.onColumnsDisplayChange(
                        columnsDisplayConfig.defaultColumnKeys || [],
                      );
                    }
                  }}
                >
                  Reset To Default
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setDialogIsOpen(false);
                  }}
                >
                  Close
                </button>
              </>
            ) : undefined
          }
        />
      )}
    </>
  );
};

export default DataGrid;
